import { getEntity } from '@lib/api';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useParams } from 'react-router-dom';
import { InvoiceDataType } from './SummaryInvoiceModal';
import { Loading } from '@components/atomic/atoms';
import { formatToDecimals } from '@lib/helpers/formatting';
import StopLightWithMessage from './StopLightWithMessage';

interface SummaryInvoiceModalInfoPositionDetailsProps {
	invoiceData: InvoiceDataType;
}

const SummaryInvoiceModalInfoPositionDetails: React.FC<SummaryInvoiceModalInfoPositionDetailsProps> = ({ invoiceData }) => {
	const { projectId } = useParams<{ projectId: string }>();
	const { data: positionDetails, isPending: isPendingPositionDetails } = useQuery({
		queryKey: ['invoicePositionDetails', projectId, invoiceData.id],
		queryFn: () => {
			return getEntity('invoices', projectId, invoiceData.id);
		},
		enabled: !!invoiceData.id,
		select: (data) => {
			return data.data.positions;
		},
	});

	if (isPendingPositionDetails) {
		return <Loading fixedHeight="25vh" />;
	}
	const fields = [
		{
			key: 'internalTaxonomy',
			label: 'Classificazione interna',
		},
		{
			key: 'positionDescription',
			label: 'Descrizione Posizione',
		},
		{
			key: 'projectExpenseItemNameNoticeExpenseItemName',
			label: 'Voce di spesa',
		},
		{
			key: 'projectExpenseItemName',
			label: 'Sottovoce di spesa',
		},
		{
			key: 'quotaNumber',
			label: 'Quota',
		},
		{
			key: 'quotaAmount',
			label: 'Importo Quota €',
		},
		{
			key: 'plantCode',
			label: 'Codice Impianto',
		},
		{
			key: 'plantType',
			label: 'Tipo Impianto',
		},
		{
			key: 'plantName',
			label: 'Denominazione Impianto',
		},
		{
			key: 'ot',
			label: 'Centro di costo',
		},
		{
			key: 'checkAdvertising',
			label: 'Check Pubblicità',
		},
		{
			key: 'checkDNSH',
			label: 'Check DNSH',
		},
		{
			key: 'taggableSerialNumber',
			label: 'Matricola Bene',
		},
		{
			key: 'taggableSerialNumberDescription',
			label: 'Descrizione Bene',
		},
		{
			key: 'materialCode',
			label: 'Codice Materiale',
		},
		{
			key: 'checkTaggable',
			label: 'Check Etichettatura',
		},
	];

	const getQuotaValueByKey = (
		quota: {
			id: string;
			projectExpenseItem: {
				name: string;
				noticeExpenseItem: {
					name: string;
				};
			};
			quotaNumber: string;
			amount: number;
			plant?: {
				plantCode: string;
				plantType: string;
				plantName: string;
			};
			ot?: {
				code: string;
			};
			advertising?: {
				color: string;
			};
			otplant?: {
				checkliststatus?: {
					color: string;
				};
			};
			taggable?: {
				serialNumber: string;
				description: string;
				color: string;
			};
			material?: {
				code: string;
			};
		},
		key: string,
	) => {
		switch (key) {
			case 'projectExpenseItemNameNoticeExpenseItemName':
				return quota.projectExpenseItem.noticeExpenseItem.name;
			case 'projectExpenseItemName':
				return quota.projectExpenseItem.name;
			case 'quotaNumber':
				return quota.quotaNumber;
			case 'quotaAmount':
				return formatToDecimals(quota.amount);
			case 'plantCode':
				return quota.plant?.plantCode;
			case 'plantType':
				return quota.plant?.plantType;
			case 'plantName':
				return quota.plant?.plantName;
			case 'ot':
				return quota.ot?.code;
			case 'checkAdvertising':
				return quota.advertising?.color;
			case 'checkDNSH':
				return quota.otplant?.checkliststatus?.color;
			case 'taggableSerialNumber':
				return quota.taggable?.serialNumber;
			case 'taggableSerialNumberDescription':
				return quota.taggable?.description;
			case 'materialCode':
				return quota.material?.code;
			case 'checkTaggable':
				return quota.taggable?.color;
			default:
				return null;
		}
	};

	return positionDetails ? (
		<>
			<hr />
			<div>
				<h4>Dettaglio posizioni</h4>

				{positionDetails.map((positionDetail) => {
					return positionDetail.quotas.map((quota, index) => {
						return (
							<div
								key={quota.id}
								style={{
									display: 'grid',
									gridTemplateColumns: 'auto 1fr',
									marginBottom: '3rem',
								}}
							>
								<div
									style={{
										fontWeight: 700,
										backgroundColor: '#EDF0F7',
										padding: '1rem',
										borderTopLeftRadius: '8px',
										borderBottomLeftRadius: '8px',
									}}
								>
									Posizione
									<br />
									{positionDetail.position}
								</div>
								<div
									style={{
										padding: '1rem',
										display: 'grid',
										gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
										gap: '1rem',
									}}
								>
									{fields.map((field) => {
										return (
											<div
												key={field.key}
												style={{
													padding: '0.5rem',
													fontSize: '14px',
												}}
											>
												<div
													style={{
														fontWeight: 700,
													}}
												>
													{field.label}
												</div>
												{field.key.startsWith('check') ? (
													<StopLightWithMessage checklistColor={getQuotaValueByKey(quota, field.key)} />
												) : (
													<div>{getQuotaValueByKey(quota, field.key) ?? 'N.D.'}</div>
												)}
											</div>
										);
									})}
								</div>
							</div>
						);
					});
				})}
			</div>
		</>
	) : null;
};

export default SummaryInvoiceModalInfoPositionDetails;
